import { create } from "zustand";
import type {
  PayoutMethodType,
  PayoutInput,
  RecipientInput,
} from "../interfaces/payout";
import type { RecipientDestination } from "../api/recipients";
import type { FundingMethod } from "../utils/sendCatalog";
import type { EstimateOption } from "../interfaces/payout";

export interface ComposeSelection {
  method: PayoutMethodType;
  option: EstimateOption | null;
  payout: PayoutInput;
}

export interface FundingInstrument {
  paymentMethodId: string;
  brand: string;
  last4: string;
}

interface ComposeState {
  originCountry: string | null;
  setOriginCountry: (country: string) => void;
  destinationCountry: string | null;
  setDestinationCountry: (country: string) => void;
  destinationCurrency: string | null;
  setDestinationCurrency: (currency: string) => void;
  sourceCurrency: string | null;
  setSourceCurrency: (currency: string) => void;
  funding: FundingMethod | null;
  setFunding: (funding: FundingMethod | null) => void;
  fundingInstrument: FundingInstrument | null;
  setFundingInstrument: (instrument: FundingInstrument | null) => void;
  selection: ComposeSelection | null;
  setSelection: (selection: ComposeSelection | null) => void;
  recipient: RecipientInput | null;
  setRecipient: (recipient: RecipientInput | null) => void;
  /** Habitual payout destinations from a recipient-book pick. */
  bookDestinations: RecipientDestination[];
  setBookDestinations: (destinations: RecipientDestination[]) => void;
  resetCompose: () => void;
}

const initialState = {
  originCountry: null as string | null,
  destinationCountry: null as string | null,
  destinationCurrency: null as string | null,
  sourceCurrency: null as string | null,
  funding: null as FundingMethod | null,
  fundingInstrument: null as FundingInstrument | null,
  selection: null as ComposeSelection | null,
  recipient: null as RecipientInput | null,
  bookDestinations: [] as RecipientDestination[],
};

export const useComposeStore = create<ComposeState>((set) => ({
  ...initialState,
  setOriginCountry: (originCountry) => set(() => ({ originCountry })),
  setDestinationCountry: (destinationCountry) => set(() => ({ destinationCountry })),
  setDestinationCurrency: (destinationCurrency) =>
    set(() => ({ destinationCurrency })),
  setSourceCurrency: (sourceCurrency) => set(() => ({ sourceCurrency })),
  setFunding: (funding) => set(() => ({ funding })),
  setFundingInstrument: (fundingInstrument) => set(() => ({ fundingInstrument })),
  setSelection: (selection) => set(() => ({ selection })),
  setRecipient: (recipient) => set(() => ({ recipient })),
  setBookDestinations: (bookDestinations) => set(() => ({ bookDestinations })),
  resetCompose: () => set(() => ({ ...initialState })),
}));
